import { GTransition } from 'components/basic-blocks'
import { UserHeader } from 'layout/navbar/user-header'
import User01 from '@untitled-ui/icons-react/build/esm/User01'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useUiStore, useUserStore } from 'store'
import Logo from '../../assets/img/cg-logo-update.svg'
import LogoWhite from '../../assets/img/cg-logo-white.svg'
import { AdminAccessTypes, hasAdminAccess } from '../../utils/admin'
import { AdminMenu } from './menus'
import { CollapseSidebar } from './menus/collapse-sidebar'
import { NavItem } from './nav-item'

/**
 * The admin rail — today's hand-rolled shell, unchanged.
 *
 * It is deliberately NOT the DS `Sidebar` (DEV-2392 covers the client rail
 * only): that rail renders a flat `pages` list under two fixed headings
 * ("Reporting" / "Protection"), which cannot express the admin menu's four
 * arbitrary groups, its `react-icons` glyphs or the Team + user-header footer
 * below them.
 *
 * Everything here — including the overlay scrollbar thumb and the GTransition
 * wrapper — was lifted out of `sidebar.tsx` verbatim when the client rail was
 * replaced, so this half of the split renders exactly what it did before.
 */
export const AdminSidebar = () => {
	const desktopSidebarCollapsed = useUiStore(
		(state) => state.desktopSidebarCollapsed
	)
	const user = useUserStore((state) => state.user)

	const navContainerRef = useRef<HTMLDivElement>(null)
	const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined
	)
	const [navScroll, setNavScroll] = useState({
		thumbHeight: 0,
		thumbTop: 0,
		isScrolling: false
	})

	const updateNavThumb = useCallback(() => {
		const el = navContainerRef.current
		if (!el) return
		const { scrollTop, scrollHeight, clientHeight } = el
		if (scrollHeight <= clientHeight) {
			setNavScroll((prev) =>
				prev.thumbHeight === 0 && prev.thumbTop === 0
					? prev
					: { ...prev, thumbHeight: 0, thumbTop: 0 }
			)
			return
		}
		const ratio = clientHeight / scrollHeight
		const thumbHeight = Math.max(24, clientHeight * ratio)
		const thumbTop =
			(scrollTop / (scrollHeight - clientHeight)) *
			(clientHeight - thumbHeight)
		setNavScroll((prev) => ({ ...prev, thumbHeight, thumbTop }))
	}, [])

	useEffect(() => {
		updateNavThumb()
		const el = navContainerRef.current
		if (!el) return
		const ro = new ResizeObserver(() => updateNavThumb())
		ro.observe(el)
		const inner = el.firstElementChild
		if (inner) ro.observe(inner)
		return () => ro.disconnect()
	}, [updateNavThumb])

	const handleNavScroll = useCallback(() => {
		updateNavThumb()
		setNavScroll((prev) =>
			prev.isScrolling ? prev : { ...prev, isScrolling: true }
		)
		if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
		scrollTimeoutRef.current = setTimeout(() => {
			setNavScroll((prev) => ({ ...prev, isScrolling: false }))
		}, 800)
	}, [updateNavThumb])

	return (
		<>
			<div
				className={`flex items-center px-4 pt-6 pb-3.5 ${
					!desktopSidebarCollapsed ? 'justify-between' : 'justify-center'
				}`}
			>
				{!desktopSidebarCollapsed && (
					<div className="pl-2.5">
						{/* Theme-reactive logo: CSS (App.css dark block) shows exactly one. */}
						<img className="app-logo-light h-5 w-auto" src={Logo} alt="logo" />
						<img className="app-logo-dark h-5 w-auto" src={LogoWhite} alt="logo" />
					</div>
				)}
				<CollapseSidebar />
			</div>

			<div
				className={`relative flex flex-col flex-grow min-h-0 duration-400 transition-all ${
					!desktopSidebarCollapsed ? 'w-[240px]' : 'w-auto'
				}`}
			>
				<div
					ref={navContainerRef}
					onScroll={handleNavScroll}
					className="sidebar-nav-scroll z-40 overflow-y-auto-important overflow-x-hidden rounded-t-[12px] flex flex-col justify-between flex-grow min-h-0 px-4"
				>
					<GTransition show={true} swap>
						<AdminMenu />
					</GTransition>
				</div>
				{navScroll.thumbHeight > 0 && (
					<div
						aria-hidden="true"
						className={`pointer-events-none absolute right-1 w-[3px] rounded-full bg-brand transition-opacity duration-300 ${
							navScroll.isScrolling ? 'opacity-50' : 'opacity-0'
						}`}
						style={{
							height: `${navScroll.thumbHeight}px`,
							top: `${navScroll.thumbTop}px`
						}}
					/>
				)}
			</div>

			<div
				className={`divide-y-2 px-4 gap-4 divide-t-bar pb-5 z-50 flex flex-col justify-end ${
					!desktopSidebarCollapsed ? 'w-[240px]' : 'w-auto'
				}`}
			>
				<div className="pt-6">
					{hasAdminAccess(user, AdminAccessTypes.view_cg_staff) && (
						<NavItem label="Team" to={() => '/admin/team'} icon={User01} />
					)}
					<UserHeader />
				</div>
			</div>
		</>
	)
}
